import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Type, RotateCcw } from "lucide-react";

const SECTIONS = [
  {
    title: "Hero section",
    fields: [
      { key: "hero_badge", label: "Badge (top pill)" },
      { key: "hero_cta_primary", label: "Primary button" },
      { key: "hero_cta_secondary", label: "Secondary button" },
      { key: "hero_stat1_number", label: "Stat 1 — number", inline: true },
      { key: "hero_stat1_label", label: "Stat 1 — label", inline: true },
      { key: "hero_stat2_number", label: "Stat 2 — number", inline: true },
      { key: "hero_stat2_label", label: "Stat 2 — label", inline: true },
      { key: "hero_card_title", label: "Floating card title" },
      { key: "hero_card_subtitle", label: "Floating card subtitle" },
    ],
  },
  {
    title: "Menu section",
    fields: [
      { key: "menu_eyebrow", label: "Eyebrow (small caps)" },
      { key: "menu_heading", label: "Heading" },
      { key: "menu_description", label: "Description", textarea: true },
    ],
  },
  {
    title: "About strip",
    fields: [
      { key: "about_1_title", label: "Item 1 — title", inline: true },
      { key: "about_1_text", label: "Item 1 — text", inline: true },
      { key: "about_2_title", label: "Item 2 — title", inline: true },
      { key: "about_2_text", label: "Item 2 — text", inline: true },
      { key: "about_3_title", label: "Item 3 — title", inline: true },
      { key: "about_3_text", label: "Item 3 — text", inline: true },
    ],
  },
  {
    title: "Booking form",
    fields: [
      { key: "booking_eyebrow", label: "Eyebrow" },
      { key: "booking_heading", label: "Heading" },
      { key: "booking_description", label: "Description", textarea: true },
      { key: "booking_submit_label", label: "Submit button label" },
      { key: "booking_success_title", label: "Success — title" },
      { key: "booking_success_text", label: "Success — body (use {name} for the customer's name)", textarea: true },
    ],
  },
];

export default function ContentTab({ config, onReload }) {
  const [content, setContent] = useState(config?.content || {});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setContent(config?.content || {}); setDirty(false); }, [config]);

  const setK = (k, v) => { setContent((c) => ({ ...c, [k]: v })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/config", { content });
      toast.success("Content saved");
      setDirty(false);
      onReload();
    } catch {
      toast.error("Save failed");
    }
    setSaving(false);
  };

  const resetOne = (k, defaultValue) => setK(k, defaultValue);

  return (
    <div className="space-y-6" data-testid="admin-content-tab">
      <Card className="border-stone-100">
        <CardContent className="p-6 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-sky-50 grid place-items-center">
              <Type className="h-5 w-5 text-blue-800" />
            </div>
            <div>
              <h3 className="font-serif-display text-2xl">Website copy</h3>
              <p className="text-sm text-stone-500">Every headline, label, and blurb your customers see — reword any of it to match your voice.</p>
            </div>
          </div>
          <Button
            data-testid="admin-content-save"
            onClick={save}
            disabled={saving || !dirty}
            className="rounded-full h-11 bg-blue-900 hover:bg-blue-800 text-white px-6"
          >
            {saving ? "Saving…" : dirty ? "Save all changes" : "Saved"}
          </Button>
        </CardContent>
      </Card>

      {SECTIONS.map((section) => (
        <Card key={section.title} className="border-stone-100">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest text-amber-500 mb-4">{section.title}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {section.fields.map((f) => (
                <div key={f.key} className={f.textarea ? "md:col-span-2" : ""}>
                  <Label className="text-xs text-stone-500 mb-1 flex items-center justify-between">
                    <span>{f.label}</span>
                    <button
                      type="button"
                      onClick={() => resetOne(f.key, config?.content?.[f.key] || "")}
                      className="text-stone-300 hover:text-stone-500"
                      title="Revert this field"
                    >
                      <RotateCcw className="h-3 w-3" />
                    </button>
                  </Label>
                  {f.textarea ? (
                    <Textarea
                      rows={3}
                      value={content[f.key] || ""}
                      onChange={(e) => setK(f.key, e.target.value)}
                      data-testid={`content-${f.key}`}
                    />
                  ) : (
                    <Input
                      value={content[f.key] || ""}
                      onChange={(e) => setK(f.key, e.target.value)}
                      data-testid={`content-${f.key}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
